import { Button, ButtonVariantOption } from "@/components/Button";
import { Label } from "@/components/Label";
import { CustomModal } from "@/components/Modal";
import { CTextFieldIdentifyName } from "@/components/TextField/CTextFieldIdentifyName";
import { PageTitle } from "@/components/Title";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { PutIdentificationNameAPI } from "@/features/allMatters/api/PutIdentificationNameAPI";
import { useApiClient } from "@/hooks/useApiClient";
import { useNavigate } from "react-router-dom";
import { type AxiosResponse, HttpStatusCode, isAxiosError } from "axios";
import { toast } from "react-toastify";
import { useEffect } from "react";
import { type RequestDetailAndRealEstates } from "../types";

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleClose: () => void;
  pictureRequestId: number;
  data: RequestDetailAndRealEstates | undefined;
}

/**
 * 識別名の編集ボタン押下時の表示されるモーダル。
 * @param {IProps} props
 * @returns
 */
export const AllMattersEditModal: React.FC<IProps> = (props: IProps) => {
  const { open, setOpen, handleClose, pictureRequestId, data } = props;

  // React-Hook-Form用スキーマ定義
  const PostDataSchema = z.object({
    identifyName: z
      .string()
      .trim()
      .min(1, { message: "識別名を入力してください" })
      .max(256, { message: "256文字まで入力可能です" }),
  });

  // 定義したZodのスキーマをTypescriptの型に変換
  type PostRequestData = z.infer<typeof PostDataSchema>;

  const reactHookForm = useForm<PostRequestData>({
    mode: "all",
    defaultValues: {
      identifyName: data?.identificationName ?? "",
    },
    resolver: zodResolver(PostDataSchema),
  });

  useEffect(() => {
    reactHookForm.setValue("identifyName", data?.identificationName ?? "");
  }, []);

  const { apiClient } = useApiClient();
  const navigator = useNavigate();

  // 保存を押下した時の処理
  const handleClickSave = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void => {
    handleExecuteSubmit(event);
  };

  // 保存処理
  const handleExecuteSubmit = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void => {
    PutIdentificationNameAPI(apiClient, pictureRequestId, {
      identificationName: reactHookForm.getValues("identifyName"),
    })
      .then((response) => {
        toast.success("識別名の更新が完了しました。");
        navigator(`/allmatters/detail/${response.id}/`);
      })
      .catch((error) => {
        if (
          isAxiosError<AxiosResponse>(error) &&
          (error.response?.status === HttpStatusCode.BadRequest ||
            error.response?.status === HttpStatusCode.NotFound)
        ) {
          toast.error("識別名の更新ができませんでした。");
          return;
        }
        if (
          isAxiosError<AxiosResponse>(error) &&
          (error.response?.status ?? HttpStatusCode.InternalServerError) >=
            HttpStatusCode.InternalServerError
        ) {
          toast.error(
            "識別名の更新に失敗しました。しばらく経ってから再度お試しください。"
          );
        }
      })
      .finally(() => {
        reactHookForm.reset();
        setOpen(false);
        handleClose();
      });
  };

  // 閉じるボタン押下時の処理
  const handleClickClose = (): void => {
    reactHookForm.clearErrors();
    setOpen(false);
  };

  return (
    <>
      <CustomModal
        isOpen={open}
        handleClose={(
          event: Record<string, unknown>,
          reason: "backdropClick" | "escapeKeyDown"
        ): void => {
          if (
            reason &&
            (reason === "backdropClick" || reason === "escapeKeyDown")
          ) {
            return;
          }
          setOpen(false);
        }}
        ariaLabelledby="name-modal"
        ariaDescribedby="name-modal-description"
        width={"350px"}
        maxHeight={"90vh"}
        overflow="scroll"
      >
        <Stack direction={"column"} spacing={3}>
          {/* タイトル */}
          <PageTitle>編集</PageTitle>

          {/* テキスト */}
          <Stack>
            <Label>識別名</Label>
            <CTextFieldIdentifyName
              name={"identifyName"}
              control={reactHookForm.control}
              size={SizingWrapperStyle.INHERIT}
              hideHelpIcon={true}
              autoFocus={true}
            />
          </Stack>
          {/* ボタン */}
          <Stack
            direction={"row"}
            spacing={2}
            marginTop={2}
            justifyContent={"space-between"}
          >
            <Button
              label="閉じる"
              onClick={handleClickClose}
              size={SizingWrapperStyle.INHERIT}
              variant={ButtonVariantOption.Outlined}
            />
            <Button
              label="保存"
              onClick={handleClickSave}
              disabled={!reactHookForm.formState.isValid}
              size={SizingWrapperStyle.INHERIT}
              variant={ButtonVariantOption.Contained}
            />
          </Stack>
        </Stack>
      </CustomModal>
    </>
  );
};
